import type { ReactNode } from "react";
import { errorMessage } from "@/shared/lib/errors";
import { cx } from "@/shared/lib/cx";
import { Icon, type IconName } from "../Icon";

type AlertTone = "info" | "danger" | "success" | "warning";
const ALERT_ICON: Record<AlertTone, IconName> = { info: "info", danger: "xCircle", success: "checkCircle", warning: "alert" };

export function Alert({ tone = "info", title, children, className, action }: {
  tone?: AlertTone; title?: ReactNode; children?: ReactNode; className?: string;
  /** Button / link on the right. */
  action?: ReactNode;
}) {
  return (
    <div className={cx("alert", `alert-${tone}`, className)} role={tone === "danger" ? "alert" : "status"}>
      <Icon name={ALERT_ICON[tone]} size={17} />
      <div className="alert-body">
        {title && <span className="alert-title">{title}</span>}
        {children && <div>{children}</div>}
      </div>
      {action}
    </div>
  );
}

/** Danger alert with a readable message for `error`; nothing when there is none. */
export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null;
  return <Alert tone="danger">{errorMessage(error)}</Alert>;
}

export function Spinner({ inline }: { inline?: boolean }) {
  if (inline) return <span className="spinner" aria-label="Đang tải" />;
  return <div className="loading-block"><span className="spinner spinner-lg" aria-label="Đang tải" /></div>;
}

/** Shimmering placeholder while content loads. */
export function Skeleton({ width = "100%", height = 12, className }: { width?: string | number; height?: number; className?: string }) {
  return <span className={cx("skeleton", className)} style={{ width, height }} aria-hidden />;
}

export function EmptyState({ title, description, action, icon = "inbox", compact }: {
  title: ReactNode; description?: ReactNode; action?: ReactNode; icon?: IconName; compact?: boolean;
}) {
  return (
    <div className={cx("empty", compact && "empty-compact")}>
      <span className="empty-icon"><Icon name={icon} size={22} /></span>
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
}
